import { useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { IconButton, Box, MenuItem } from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import { Transforms, Element } from 'slate';

import { DropdownMenu } from 'common/components/DropdownMenu';
import { useContentEditor } from 'common/components/ContentEditor/hooks/useContentEditor';
import { options } from 'common/components/ContentEditor/options';
import { asEditor } from 'common/components/ContentEditor/utils/editorHelpers';

import { IMenuPosition } from './types';
import { getCurrentBlockMenu, getMenuPosition } from './utils';

export const EditorBlockMenu = () => {
    const editor = useContentEditor();
    const { t: translate } = useTranslation();
    const [position, setPosition] = useState<IMenuPosition>();

    const node = editor.api.block({ highest: true })?.[0];

    const element = Element.isElement(node) ? node : null;

    const menuRef = useCallback(
        (menuEl: HTMLElement | null) => {
            if (menuEl !== null && element) {
                setTimeout(() => {
                    setPosition(getMenuPosition(editor, menuEl, element));
                }, 10);
            }
        },
        [editor, element]
    );

    if (!element) return null;

    const { top, left } = position || {};

    const BlockMenuComponent = getCurrentBlockMenu(element);

    if (!BlockMenuComponent) return null;

    const handleDelete = () => {
        Transforms.removeNodes(asEditor(editor));
    };

    const editorBlockMenu = (
        <Box
            ref={menuRef}
            sx={{
                left: left,
                position: 'absolute',
                top: top,
            }}
        >
            <DropdownMenu
                anchor={
                    <IconButton className="actions" size="large">
                        <MoreVert />
                    </IconButton>
                }
                disableReactTreeClickAway={false}
            >
                <BlockMenuComponent {...options} />

                <MenuItem onClick={handleDelete}>
                    {translate('delete')}
                </MenuItem>
            </DropdownMenu>
        </Box>
    );

    return ReactDOM.createPortal(editorBlockMenu, document.body);
};
